import React, { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });

// Troca de plano self-service (admin da empresa).
// Vigência: próxima renovação; com assinatura ativa o valor do débito
// automático é atualizado no Mercado Pago.
export default function ChangePlanDialog({
  open,
  onClose,
  currentPlan,
  subscriptionActive,
  onChanged,
}) {
  const { getPlanList } = usePlans();

  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedPlanId("");
    const fetchPlans = async () => {
      setLoading(true);
      try {
        const list = await getPlanList();
        // defesa extra: plano sem valor de cobrança nunca é listado
        setPlans(
          (Array.isArray(list) ? list : []).filter(
            (p) => Number(p.value) > 0
          )
        );
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedPlan = plans.find(
    (p) => String(p.id) === String(selectedPlanId)
  );

  const handleConfirm = async () => {
    if (!selectedPlan) return;
    setSaving(true);
    try {
      await api.put("/subscription/plan", { planId: selectedPlan.id });
      toast.success(
        i18n.t("changePlanDialog.changed", { plan: selectedPlan.name })
      );
      onClose();
      if (onChanged) onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{i18n.t("changePlanDialog.title")}</DialogTitle>
      <DialogContent dividers>
        {currentPlan && (
          <Typography variant="body2" gutterBottom>
            {i18n.t("changePlanDialog.currentPlan")}{" "}
            <strong>{currentPlan.name}</strong> —{" "}
            {i18n.t("changePlanDialog.perMonth", {
              value: formatCurrency(currentPlan.value),
            })}
          </Typography>
        )}

        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <TextField
            select
            fullWidth
            variant="outlined"
            margin="dense"
            label={i18n.t("changePlanDialog.newPlan")}
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
          >
            {plans
              .filter((p) => !currentPlan || p.id !== currentPlan.id)
              .map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {i18n.t("changePlanDialog.planOption", {
                    name: plan.name,
                    value: formatCurrency(plan.value),
                    users: plan.users,
                    connections: plan.connections,
                  })}
                </MenuItem>
              ))}
          </TextField>
        )}

        {selectedPlan && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 8 }}
          >
            {i18n.t("changePlanDialog.newValueNote")}{" "}
            <strong>
              {i18n.t("changePlanDialog.perMonth", {
                value: formatCurrency(selectedPlan.value),
              })}
            </strong>
            .
            {subscriptionActive &&
              ` ${i18n.t("changePlanDialog.autoDebitUpdate")}`}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {i18n.t("common.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!selectedPlan || saving}
        >
          {saving ? (
            <CircularProgress size={20} />
          ) : (
            i18n.t("changePlanDialog.confirm")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
